import type { GeneratedDoorPlacement, DoorDescriptor } from "building-types/doors/doorTypes";
import type { DoorPlacement, DoorPlacementNoExpr } from "building-types/openings/opening";
import type { AwningSettings } from "building-types/settings/awningSettings";
import type { DoorWallFramingSettings } from "building-types/settings/doorWallFramingSettings";
import type { DoorWallWrappingSettings } from "building-types/settings/doorWallWrappingSettings";
import type { ExteriorDoorCasingSettings } from "building-types/settings/exteriorDoorCasingSettings";
import type { PartialHomeSettings } from "building-types/settings/homeSettings";
import type { InteriorDoorCasingSettings } from "building-types/settings/interiorDoorCasingSettings";
import type { MergeSettingsForEntityParams } from "building-types/settings/mergeSettingsForEntityParams";
import type { AllDoorViewNoteTable } from "building-types/settings/viewNotesSettings";
import { mergeSettingsForEntity } from "doc/mergeSettingsSet";
import { getDocEntityIdFromGeneratedRoomLayoutEligibleEntity } from "doc/rooms/layout/roomLayoutTools";
import { defaultDoorViewNotes } from "doc/settings/defaultViewNoteSettings";
import { mergeViewNoteTables } from "doc/utils/viewNotesSettingsTools";
import type { DoorHomeSettings, MergedDoorSettings } from "./mergedDoorSettings";

/**
 * The order in which these settings are merged is as follows:
 * 1. home settings
 * 2. catalog settings
 * 3. targeted settings
 * 4. door instance
 */

export interface MergeSettingsForDoorParams extends Omit<MergeSettingsForEntityParams, "homeSettings"> {
  readonly door?: DoorPlacement | DoorPlacementNoExpr | GeneratedDoorPlacement;
  readonly descriptor?: DoorDescriptor;
  readonly homeSettings: DoorHomeSettings;
}

export default function getMergedDoorSettings({
  door,
  settings,
  descriptor,
  homeSettings,
  optionChoices,
  entitySettings,
  activeSettingsSets,
}: MergeSettingsForDoorParams): MergedDoorSettings {
  const { exteriorDoorCasing, interiorDoorCasing, awning, doorFraming, doorWrapping } = homeSettings ?? {};

  const openingId = getDocEntityIdFromGeneratedRoomLayoutEligibleEntity(door);

  const mergedTargetedSettings: PartialHomeSettings = {};

  if (door) {
    Object.assign(
      mergedTargetedSettings,
      mergeSettingsForEntity(entitySettings, openingId, activeSettingsSets, settings, optionChoices),
    );
  }

  let mergedExteriorCasingSettings: ExteriorDoorCasingSettings | null = null;

  mergedExteriorCasingSettings = {
    ...exteriorDoorCasing,
    ...descriptor?.exteriorCasing,
    ...mergedTargetedSettings?.exteriorDoorCasing,
    ...door?.exteriorCasing,
    viewNotes: mergeViewNoteTables(
      exteriorDoorCasing?.viewNotes,
      descriptor?.exteriorCasing?.viewNotes,
      mergedTargetedSettings.exteriorDoorCasing?.viewNotes,
      door?.exteriorCasing?.viewNotes,
    ),
  } as ExteriorDoorCasingSettings;

  const mergedInteriorCasingSettings: InteriorDoorCasingSettings = {
    ...interiorDoorCasing,
    ...descriptor?.interiorCasing,
    ...mergedTargetedSettings?.interiorDoorCasing,
    ...door?.interiorCasing,
  };

  const mergedAwningSettings: AwningSettings = {
    ...awning,
    ...mergedTargetedSettings?.awning,
    ...door?.awningSettings,
    viewNotes: mergeViewNoteTables(
      awning?.viewNotes,
      mergedTargetedSettings.awning?.viewNotes,
      door?.awningSettings?.viewNotes,
    ) as AllDoorViewNoteTable,
  };

  const mergedFramingSettings: DoorWallFramingSettings = {
    ...doorFraming,
    ...descriptor?.framing,
    ...mergedTargetedSettings?.doorFraming,
    ...door?.framing,
  };

  const mergedWrappingSettings: DoorWallWrappingSettings = {
    ...doorWrapping,
    ...descriptor?.wrapping,
    ...mergedTargetedSettings?.doorWrapping,
    ...door?.wrapping,
  };

  const mergedDoorViewNoteTable = mergeViewNoteTables(defaultDoorViewNotes, descriptor?.viewNotes, door?.viewNotes);

  const viewNoteTable = mergeViewNoteTables(
    mergedDoorViewNoteTable,
    mergedAwningSettings.viewNotes,
    mergedExteriorCasingSettings.viewNotes,
  ) as AllDoorViewNoteTable;

  return {
    exteriorCasing: mergedExteriorCasingSettings as any as Required<ExteriorDoorCasingSettings>,
    interiorCasing: mergedInteriorCasingSettings as any as Required<InteriorDoorCasingSettings>,
    awningSettings: mergedAwningSettings as any as Required<AwningSettings>,
    framing: mergedFramingSettings,
    wrapping: mergedWrappingSettings,
    swingSide: door?.swingSide || descriptor?.swingSide,
    viewNoteTable,
  };
}
